import { useQuery } from "@tanstack/react-query"
import { supabase } from "@/integrations/supabase/client"
import { useAuth } from "./useAuth"
import { addDays, eachDayOfInterval, startOfDay } from "date-fns"

export interface FinancialReportData {
  revenues: any[]
  expenses: any[]
  charges: any[]
  suppliers: any[]
  totalRevenues: number
  totalExpenses: number
  balance: number
  chargesStats: {
    paid: number
    pending: number
    overdue: number
    canceled: number
  }
  expensesByCategory: Array<{ category: string; amount: number }>
  revenuesByCategory: Array<{ category: string; amount: number }>
  topSuppliers: Array<{ name: string; total: number }>
}

export const useFinancialReportData = (startDate: Date, endDate: Date) => {
  const { profile } = useAuth()

  return useQuery({
    queryKey: ["financial-report-data", profile?.condominium_id, startDate, endDate],
    queryFn: async () => {
      if (!profile?.condominium_id) throw new Error("Condominium ID not found")

      const start = startDate.toISOString().split('T')[0]
      const end = endDate.toISOString().split('T')[0]

      // Fetch all financial data in parallel
      const [revenuesRes, expensesRes, chargesRes, suppliersRes] = await Promise.all([
        supabase
          .from("financial_revenues")
          .select("*")
          .eq("condominium_id", profile.condominium_id)
          .gte("revenue_date", start)
          .lte("revenue_date", end)
          .order("revenue_date", { ascending: false }),
        
        supabase
          .from("financial_expenses")
          .select(`
            *,
            supplier:suppliers(name, category)
          `)
          .eq("condominium_id", profile.condominium_id)
          .gte("expense_date", start)
          .lte("expense_date", end)
          .order("expense_date", { ascending: false }),
        
        supabase
          .from("financial_charges")
          .select(`
            *,
            unit:units(unit_number, building),
            resident:profiles(full_name)
          `)
          .eq("condominium_id", profile.condominium_id)
          .gte("due_date", start)
          .lte("due_date", end)
          .order("due_date", { ascending: false }),
        
        supabase
          .from("suppliers")
          .select("*")
          .eq("condominium_id", profile.condominium_id)
          .eq("is_active", true)
      ])

      if (revenuesRes.error) throw revenuesRes.error
      if (expensesRes.error) throw expensesRes.error
      if (chargesRes.error) throw chargesRes.error
      if (suppliersRes.error) throw suppliersRes.error

      const revenues = revenuesRes.data || []
      const expenses = expensesRes.data || []
      const charges = chargesRes.data || []
      const suppliers = suppliersRes.data || []

      // Calculate totals
      const totalRevenues = revenues.reduce((sum, r) => sum + r.amount_cents, 0)
      const totalExpenses = expenses
        .filter(e => e.status === 'paid')
        .reduce((sum, e) => sum + e.amount_cents, 0)
      const balance = totalRevenues - totalExpenses

      // Calculate charges stats
      const chargesStats = {
        paid: charges.filter(c => c.status === 'paid').reduce((sum, c) => sum + c.amount_cents, 0),
        pending: charges.filter(c => c.status === 'pending').reduce((sum, c) => sum + c.amount_cents, 0),
        overdue: charges.filter(c => c.status === 'overdue').reduce((sum, c) => sum + c.amount_cents, 0),
        canceled: charges.filter(c => c.status === 'canceled').reduce((sum, c) => sum + c.amount_cents, 0)
      }

      // Group by category
      const expensesByCategory = Object.entries(
        expenses.reduce((acc, e) => {
          const cat = e.category || 'Outros'
          acc[cat] = (acc[cat] || 0) + e.amount_cents
          return acc
        }, {} as Record<string, number>)
      ).map(([category, amount]) => ({ category, amount }))

      const revenuesByCategory = Object.entries(
        revenues.reduce((acc, r) => {
          const cat = r.category || 'Outros'
          acc[cat] = (acc[cat] || 0) + r.amount_cents
          return acc
        }, {} as Record<string, number>)
      ).map(([category, amount]) => ({ category, amount }))

      // Top suppliers
      const supplierTotals = expenses.reduce((acc, e) => {
        if (e.supplier?.name) {
          acc[e.supplier.name] = (acc[e.supplier.name] || 0) + e.amount_cents
        }
        return acc
      }, {} as Record<string, number>)

      const topSuppliers = Object.entries(supplierTotals)
        .map(([name, total]) => ({ name, total }))
        .sort((a, b) => b.total - a.total)
        .slice(0, 5)

      return {
        revenues,
        expenses,
        charges,
        suppliers,
        totalRevenues,
        totalExpenses,
        balance,
        chargesStats,
        expensesByCategory,
        revenuesByCategory,
        topSuppliers
      } as FinancialReportData
    },
    enabled: !!profile?.condominium_id && !!startDate && !!endDate
  })
}

export const useCashFlowData = (startDate: Date, endDate: Date) => {
  const { profile } = useAuth()

  return useQuery({
    queryKey: ["cash-flow-data", profile?.condominium_id, startDate, endDate],
    queryFn: async () => {
      if (!profile?.condominium_id) throw new Error("Condominium ID not found")

      const start = startDate.toISOString().split('T')[0]
      const end = endDate.toISOString().split('T')[0]

      // Fetch revenues and expenses
      const [revenuesRes, expensesRes] = await Promise.all([
        supabase
          .from("financial_revenues")
          .select("*")
          .eq("condominium_id", profile.condominium_id)
          .gte("revenue_date", start)
          .lte("revenue_date", end)
          .order("revenue_date", { ascending: true }),
        
        supabase
          .from("financial_expenses")
          .select("*")
          .eq("condominium_id", profile.condominium_id)
          .eq("status", "paid")
          .gte("expense_date", start)
          .lte("expense_date", end)
          .order("expense_date", { ascending: true })
      ])

      if (revenuesRes.error) throw revenuesRes.error
      if (expensesRes.error) throw expensesRes.error

      const revenues = revenuesRes.data || []
      const expenses = expensesRes.data || []

      // Calculate daily balance
      const days = eachDayOfInterval({ start: startDate, end: endDate })
      let runningBalance = 0

      const dailyBalance = days.map(day => {
        const dayStr = day.toISOString().split('T')[0]
        
        const dayRevenues = revenues
          .filter(r => r.revenue_date === dayStr)
          .reduce((sum, r) => sum + r.amount_cents, 0)
        
        const dayExpenses = expenses
          .filter(e => e.expense_date === dayStr)
          .reduce((sum, e) => sum + e.amount_cents, 0)
        
        const inflow = dayRevenues
        const outflow = dayExpenses
        runningBalance += (inflow - outflow)
        
        return {
          date: dayStr,
          inflow,
          outflow,
          balance: runningBalance
        }
      })

      return {
        revenues,
        expenses,
        dailyBalance,
        initialBalance: 0,
        finalBalance: runningBalance
      }
    },
    enabled: !!profile?.condominium_id && !!startDate && !!endDate
  })
}

export const useOverdueCharges = () => {
  const { profile } = useAuth()

  return useQuery({
    queryKey: ["overdue-charges", profile?.condominium_id],
    queryFn: async () => {
      if (!profile?.condominium_id) throw new Error("Condominium ID not found")

      const today = new Date().toISOString().split('T')[0]

      const { data, error } = await supabase
        .from("financial_charges")
        .select(`
          *,
          unit:units(unit_number, building),
          resident:profiles(full_name)
        `)
        .eq("condominium_id", profile.condominium_id)
        .eq("status", "overdue")
        .lt("due_date", today)
        .order("due_date", { ascending: true })

      if (error) throw error

      const charges = data || []
      const totalOverdue = charges.reduce((sum, c) => sum + c.amount_cents, 0)

      // Calculate total charges to get default rate
      const { data: allCharges } = await supabase
        .from("financial_charges")
        .select("amount_cents")
        .eq("condominium_id", profile.condominium_id)

      const totalCharges = (allCharges || []).reduce((sum, c) => sum + c.amount_cents, 0)
      const defaultRate = totalCharges > 0 ? (totalOverdue / totalCharges) * 100 : 0

      return {
        charges: charges.map(c => ({
          ...c,
          days_overdue: Math.floor(
            (new Date().getTime() - new Date(c.due_date).getTime()) / (1000 * 60 * 60 * 24)
          )
        })),
        totalOverdue,
        totalCharges,
        defaultRate
      }
    },
    enabled: !!profile?.condominium_id
  })
}

export const useSupplierReport = (supplierId: string, startDate: Date, endDate: Date) => {
  const { profile } = useAuth()

  return useQuery({
    queryKey: ["supplier-report", supplierId, startDate, endDate],
    queryFn: async () => {
      if (!profile?.condominium_id) throw new Error("Condominium ID not found")

      const start = startDate.toISOString().split('T')[0]
      const end = endDate.toISOString().split('T')[0]

      // Fetch supplier data
      const { data: supplier, error: supplierError } = await supabase
        .from("suppliers")
        .select("*")
        .eq("id", supplierId)
        .single()

      if (supplierError) throw supplierError

      // Fetch expenses for this supplier
      const { data: expenses, error: expensesError } = await supabase
        .from("financial_expenses")
        .select("*")
        .eq("supplier_id", supplierId)
        .gte("expense_date", start)
        .lte("expense_date", end)
        .order("expense_date", { ascending: false })

      if (expensesError) throw expensesError

      const expensesList = expenses || []
      const totalPaid = expensesList
        .filter(e => e.status === 'paid')
        .reduce((sum, e) => sum + e.amount_cents, 0)
      
      const totalPending = expensesList
        .filter(e => e.status === 'pending')
        .reduce((sum, e) => sum + e.amount_cents, 0)
      
      const serviceCount = expensesList.length
      const averageTicket = serviceCount > 0 ? (totalPaid + totalPending) / serviceCount : 0

      return {
        supplier,
        expenses: expensesList,
        totalPaid,
        totalPending,
        serviceCount,
        averageTicket
      }
    },
    enabled: !!profile?.condominium_id && !!supplierId && !!startDate && !!endDate
  })
}
